import React, { useState } from "react";
import {
  showError,
  showSuccess,
} from "../../../../../../../../utils/showMessage.js";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Images from "../../../../../../../../utils/images.js";
import CrossButton from "../../../../../../../common/ButtonSpinner/CrossButton.js";
import GeneralButton from "../../../../../../../common/SaveButton/GeneralButton.js";
import CustomModal from "../../../../../../../common/CustomModal/CustomModal.js";
import { ToastContainer, toast } from "react-toastify";
import { GrDocumentCsv } from "react-icons/gr";
import MuiButton from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { Tooltip } from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import { importTable } from "../../../../../../../../api/database_gui.js";

const UploadButton = styled(MuiButton)({
  boxShadow: "none",
  textTransform: "none",
  fontWeight: 600,
  fontSize: 14,
  fontStyle: "normal",
  padding: "8px 12px",
  color: "#717171",
  lineHeight: "21px",
  fontFamily: ['"Poppins"'].join(","),
});

const maxFileSize = 2 * 1024 * 1024; // 2MB in bytes

const ImportDataInTable = ({
  visible,
  onClose,
  onSave,
  dbName,
  tableName,
  appId,
  systemUserId,
  setImportData,
  loading,
  setLoading,
  databaseInfo,
}) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [fileSizeExceed, setFileSizeExceed] = useState(false);
  const [isCsv, setIsCsv] = useState(true);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.name.toLowerCase().endsWith(".csv")) {
        setIsCsv(true);
        setError("");
      } else {
        setIsCsv(false);
        setError("Please select a CSV file.");
      }
      if (selectedFile.size > maxFileSize) {
        setFileSizeExceed(true);
        setError("File size exceeds 2MB.");
      } else {
        setFileSizeExceed(false);
      }
      setFile(selectedFile);
    }
  };

  const handleConfirm = async () => {
    if (!file) {
      setError("Please select a file to import.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("db_name", dbName);
    formData.append("table_name", tableName);
    try {
      const response = await importTable(appId, systemUserId, formData);
      onSave();
      // Use backend success message if available, otherwise fallback to default
      const successMessage = response?.data?.message || "Data imported successfully!";
      showSuccess(successMessage);
      setFile(null);
    } catch (error) {
      // Use backend error message if available, otherwise fallback to default
      const errorMessage = error.message || "Error while importing data. Please try again.";
      showError(errorMessage);
    }
  };

  const downloadSampleFile = () => {
    const sampleData = [
      [
        "###############################################################################################",
      ],
      ["# Remove this Guide before uploading the file."],
      [
        "###############################################################################################",
      ],
      ["Column1", "Column2", "Column3"],
      ["SampleData1", "SampleData2", "SampleData3"],
      ["SampleData4", "SampleData5", "SampleData6"],
      ["# Guide: Fill the data according to the schema defined above."],
    ];
    const csvContent = sampleData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample.csv";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <CustomModal 
      open={visible} 
      handleClose={onClose}
      width="40%"
    >
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="d-flex align-items-center mb-1">
              {databaseInfo === "MySQL" && (
                <img src={Images.mysql} alt="MySQL" width="24" height="24" className="me-2" />
              )}
              {databaseInfo === "PostgreSQL" && (
                <img src={Images.pssql} alt="PostgreSQL" width="24" height="24" className="me-2" />
              )}
              {databaseInfo === "MSSQL" && (
                <img src={Images.mssql} alt="MSSQL" width="24" height="24" className="me-2" />
              )}
              {databaseInfo === "Oracledb" && (
                <img src={Images.oracle} alt="Oracle" width="24" height="24" className="me-2" />
              )}
              {databaseInfo === "SAPHanadb" && (
                <img src={Images.sap} alt="SAP HANA" width="24" height="24" className="me-2" />
              )}
              <h3 className="border-0 customize-column-name mb-0">
                Import Data
              </h3>
            </div>
            <p className="text-muted mb-0 fs-14px">
              Import CSV data into the <code className="bg-light px-2 py-1 rounded">{tableName}</code> table
            </p>
          </div>
          <CrossButton onClick={onClose} />
        </div>



        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleConfirm();
          }}
        >
          <div className="mb-4">
            <Form.Label className="input_label d-flex align-items-center mb-2">
              Select CSV File
              <span className="text-danger ms-1">*</span>
              <Tooltip
                title="Import Guidelines: Only CSV files are supported. Maximum file size: 2MB. Column headers should match your table schema. Download the sample file below for reference."
                placement="top-end"
              >
                <img
                  src={Images.InfoIcon}
                  alt="info"
                  width="15"
                  height="15"
                  className="cursor_pointer ms-2"
                />
              </Tooltip>
            </Form.Label>
            <Form.Control
              onChange={handleFileChange}
              type="file"
              accept=".csv"
              className={`form_input_field ${error ? 'is-invalid' : ''}`}
            />
            {error && (
              <div className="invalid-feedback d-block mt-1">
                {error}
              </div>
            )}
            <div className="text-muted fs-12px mt-1">
              Maximum file size: 2MB
            </div>
          </div>

          <Row className="pt-4 mb-3 justify-content-end">
            <Col sm="auto">
              <GeneralButton
                onClickEvent={onClose}
                className="me-1"
                value="Cancel"
                color="#505050"
                variant="outlined"
                size="large"
              />
            </Col>
            <Col sm="auto" className="me-sm-2 p-0">
              {loading ? (
                <GeneralButton
                  onClickEvent={handleConfirm}
                  variant="contained"
                  disabled={true}
                  className="me-1"
                  value={
                    <img src={Images.Loader} width="26px" height="26px" />
                  }
                  size="large"
                />
              ) : (
                <GeneralButton
                  onClickEvent={handleConfirm}
                  className="me-1"
                  value="Import"
                  variant="contained"
                  size="large"
                  disabled={fileSizeExceed || !isCsv || !file}
                />
              )}
            </Col>
          </Row>
          <hr />
          <div className="ff-poppins fs-12px">
            <div className="fs-14px">
              Please refer to CSV schema and sample file available for download below:
            </div>
            <UploadButton
              onClick={() => downloadSampleFile()}
              startIcon={<GrDocumentCsv size={20} />}
            >
              Sample File
            </UploadButton>
            (schema and guide included in the sample file)
          </div>
        </Form>
      </div>
    </CustomModal>
  );
};

export default ImportDataInTable;
